import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from '../../services/login.service';
import { Client } from '../../interfaces/Client.interface';
import { AdvGrowlService } from 'primeng-advanced-growl';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  public username: string = '';
  public password: string = '';
  public validateUserWhite: boolean = true;
  public validatePassWhite: boolean = true;
  public validateUser: boolean = true;
  public infoUser: Client;
  public textConditions: boolean = false;
  public textoAlert: string;
  public selIdioma: string = '0';
  public languajes = [
    { id: '0', name: 'Idioma' },
    { id: 'es', name: 'Español' },
    { id: 'ga', name: 'Gallego' }
  ];
  public checkDni: boolean = false;
  public showRegister: boolean = false;
  public typeLogin: string = '0';

  constructor(
    private router: Router,
    private loginService: LoginService,
    private advGrowlService: AdvGrowlService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    const savedLang = sessionStorage.getItem('lang');
    if (savedLang) {
      this.selIdioma = savedLang;
      this.translate.use(savedLang);
    }
  }

  public selectDniEmail(type: string): void {
    this.typeLogin = type;
    this.checkDni = type === '1';
    this.username = '';
  }

  public goRegister(): void {
    this.showRegister = true;
  }

  public goLogin(): void {
    this.showRegister = false;
  }

  public login(selectedLanguage: string): void {
    if (!this.validateForm()) {
      return;
    }

    this.changeLanguage(selectedLanguage);

    // Resetear mensajes de error anteriores
    this.textConditions = false;
    this.textoAlert = '';

    console.debug('Intentando login con:', {
      username: this.username,
      type: this.typeLogin,
      selectedLanguage
    });

    this.loginService.login(this.username, this.password, this.typeLogin)
      .subscribe({
        next: (response) => {
          console.debug('Respuesta de login:', response);
          if (response.success) {
            this.handleLoginSuccess(response);
          } else {
            this.handleLoginError(response.message);
          }
        },
        error: (error) => {
          console.error('Error en login:', error);
          let errorMessage = 'Error desconocido';

          switch (error.status) {
            case 401:
              errorMessage = 'Usuario o contraseña incorrectos';
              break;
            case 400:
              errorMessage = error.error && error.error.message
                ? error.error.message
                : 'Datos de inicio de sesión inválidos';
              break;
            case 500:
              errorMessage = 'Error interno del servidor. Por favor, inténtelo de nuevo más tarde.';
              break;
            case 0:
              errorMessage = 'Error de conexión. Por favor, verifique su conexión a internet.';
              break;
            default:
              errorMessage = (error.error && error.error.message) || error.message || 'Error desconocido';
          }

          this.handleLoginError(errorMessage);
        },
        complete: () => {
          console.debug('Login request completed');
        }
      });
  }

  private validateForm(): boolean {
    const isUserValid = this.validateUserLogin();
    const isPasswordValid = this.validatePasswordLogin();
    return isUserValid && isPasswordValid;
  }

  private handleLoginSuccess(response: any): void {
    try {
      if (!response.data || !response.data.user_id || !response.data.username) {
        throw new Error('La respuesta del servidor no tiene la estructura esperada');
      }

      // Crear estructura básica del cliente
      // Convertir el user_id array a string para cumplir con la interfaz Client
      const userId = Array.isArray(response.data.user_id)
        ? response.data.user_id[1].toString()
        : response.data.user_id.toString();

      const clientData: Client = {
        info: {
          id: userId,
          name: response.data.username,
          business: '',
          address: '',
          email: this.username,
          val_score: '0',
          val_score_lud: '0',
          tons: 0
        },
        level: {
          id: '1',
          name: 'Default',
          description: 'Nivel por defecto'
        },
        category: {
          id: '1',
          name: 'Default',
          description: 'Categoría por defecto'
        }
      };

      this.infoUser = clientData;
      localStorage.setItem('infoClient', JSON.stringify(clientData));
      this.router.navigate(['/home']).then(() => {
        this.advGrowlService.createSuccessMessage(
          'Inicio de sesión exitoso',
          'Bienvenido'
        );
      });
    } catch (error) {
      console.error('Error al procesar los datos del usuario:', error);
      this.advGrowlService.createErrorMessage(
        'Error al procesar los datos del usuario',
        'Error'
      );
    }
  }

  private handleLoginError(message: string): void {
    this.textConditions = true;
    this.textoAlert = message || 'Error de inicio de sesión';
    this.advGrowlService.createErrorMessage(
      this.textoAlert,
      'Error'
    );
  }

  public changeLanguage(selectedLanguage: string): void {
    if (selectedLanguage !== '0') {
      this.selIdioma = selectedLanguage;
      sessionStorage.setItem('lang', selectedLanguage);
      this.translate.use(selectedLanguage);
    }
  }

  public keypressUser(): void {
    this.validateUserWhite = true;
    this.textConditions = false;
  }

  public keypressPass(): void {
    this.textConditions = false;
    this.validatePassWhite = true;
  }

  public validateUserLogin(): boolean {
    if (!this.validateUserValue()) {
      this.validateUserWhite = false;
      return false;
    }

    if (this.username === '') {
      this.validateUserWhite = false;
      return false;
    }

    this.validateUserWhite = true;
    return true;
  }

  public validatePasswordLogin(): boolean {
    const isValid = this.password !== '' && this.password !== undefined;
    this.validatePassWhite = isValid;
    return isValid;
  }

  public validateUserValue(): boolean {
    const valueFormat = this.username.toString().toUpperCase();
    const emailRegex = /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{1,5}|[0-9]{1,3})(\]?)$/;
    const nifRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
    const nieRegex = /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;

    const isValid = emailRegex.test(valueFormat) ||
                   nifRegex.test(valueFormat) ||
                   nieRegex.test(valueFormat);

    this.validateUser = isValid;
    return isValid;
  }

  public rememberPassword(): void {
    if (this.username === '') {
      this.advGrowlService.createInfoMessage(
        'Debe introducir su email.',
        'Faltan datos'
      );
      return;
    }

    this.loginService.recoveryPassword(this.username).subscribe({
      next: () => {
        this.advGrowlService.createInfoMessage(
          'Muy pronto recibirá un email con su contraseña',
          'Enviado'
        );
      },
      error: () => {
        this.advGrowlService.createWarningMessage(
          'Error al enviar el mensaje.',
          'Error'
        );
      }
    });
  }
}
